import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useUpdateVettingConfigMutation } from '../../api/use-update-vetting-config-mutation';
import { useVettingConfigQuery } from '../../api/use-vetting-config-query';
import { formatVettingDateTime } from '../../services/vettingApplications';
import PageContainer from '../PageContainer';
import { PortalTableCell, PortalTableHead, PortalTableRow } from '../../styles/portalListTable';

const TOTAL_VETTING_QUESTIONS = 5;
const SHOW_EVERYTHING = false;

const DetailField = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
      {value ?? '-'}
    </Typography>
  </Box>
);

const ListChips = ({ values }) => {
  const items = Array.isArray(values) ? values.filter(Boolean) : [];

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {items.map((item) => (
        <Chip key={item} label={item} size="small" sx={{ borderRadius: '6px', fontWeight: 500 }} />
      ))}
    </Box>
  );
};

const createFormFromConfig = (config) => ({
  systemInstructions: config?.systemInstructions ?? '',
  questionConfig: JSON.stringify(config?.questionConfig ?? [], null, 2),
  dimensionWeights: JSON.stringify(config?.dimensionWeights ?? {}, null, 2),
  acceptThreshold: String(config?.acceptThreshold ?? ''),
  waitlistThreshold: String(config?.waitlistThreshold ?? ''),
  q5MinDimensionScore: String(config?.q5MinDimensionScore ?? ''),
  minWordCountPerAnswer: String(config?.minWordCountPerAnswer ?? ''),
  maxShortAnswersBeforeReject: String(config?.maxShortAnswersBeforeReject ?? ''),
  duplicateWindowDays: String(config?.duplicateWindowDays ?? ''),
  waitlistReapplyDays: String(config?.waitlistReapplyDays ?? ''),
  rejectReapplyDays: String(config?.rejectReapplyDays ?? ''),
  changeNote: config?.changeNote ?? '',
});

const sumNumbers = (values) => values.reduce((sum, value) => sum + Number(value ?? 0), 0);

const parseConfigForm = (form) => {
  let questionConfig;
  let dimensionWeights;

  try {
    questionConfig = JSON.parse(form.questionConfig);
  } catch (_error) {
    throw new Error('questionConfig must be valid JSON');
  }

  try {
    dimensionWeights = JSON.parse(form.dimensionWeights);
  } catch (_error) {
    throw new Error('dimensionWeights must be valid JSON');
  }

  if (!Array.isArray(questionConfig)) {
    throw new Error('questionConfig must be an array');
  }

  if (questionConfig.length !== TOTAL_VETTING_QUESTIONS) {
    throw new Error(`questionConfig must have exactly ${TOTAL_VETTING_QUESTIONS} entries`);
  }

  if (sumNumbers(questionConfig.map((question) => question.weight)) !== 100) {
    throw new Error('questionConfig weights must sum to 100');
  }

  if (
    !dimensionWeights ||
    Array.isArray(dimensionWeights) ||
    typeof dimensionWeights !== 'object'
  ) {
    throw new Error('dimensionWeights is required');
  }

  if (sumNumbers(Object.values(dimensionWeights)) !== 100) {
    throw new Error('dimensionWeights must sum to 100');
  }

  return {
    systemInstructions: form.systemInstructions.trim(),
    questionConfig,
    dimensionWeights,
    acceptThreshold: Number(form.acceptThreshold),
    waitlistThreshold: Number(form.waitlistThreshold),
    q5MinDimensionScore: Number(form.q5MinDimensionScore),
    minWordCountPerAnswer: Number(form.minWordCountPerAnswer),
    maxShortAnswersBeforeReject: Number(form.maxShortAnswersBeforeReject),
    duplicateWindowDays: Number(form.duplicateWindowDays),
    waitlistReapplyDays: Number(form.waitlistReapplyDays),
    rejectReapplyDays: Number(form.rejectReapplyDays),
    changeNote: form.changeNote.trim(),
  };
};

const VettedCommunityConfig = () => {
  const { data, isLoading, error } = useVettingConfigQuery();
  const active = data?.active;
  const history = data?.history ?? [];
  const [form, setForm] = useState(createFormFromConfig(null));
  const [formError, setFormError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const updateConfigMutation = useUpdateVettingConfigMutation({
    onSuccess: (result) => {
      toast.success(result.message || 'Vetting config updated');
      setIsEditing(false);
    },
  });

  useEffect(() => {
    if (active && !isEditing) {
      setForm(createFormFromConfig(active));
      setFormError('');
    }
  }, [active, isEditing]);

  const handleFieldChange = (field) => (event) => {
    setForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleStartEdit = () => {
    setForm(createFormFromConfig(active));
    setFormError('');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setForm(createFormFromConfig(active));
    setFormError('');
    setIsEditing(false);
  };

  const handleSaveConfig = async (event) => {
    event.preventDefault();
    setFormError('');

    try {
      const payload = parseConfigForm(form);
      await updateConfigMutation.mutateAsync(payload);
    } catch (saveError) {
      const message = saveError.message || 'Failed to update vetting config';
      setFormError(message);
      toast.error(message);
    }
  };

  return (
    <PageContainer title="Vetting Config">
      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2">
          {error.message}
        </Typography>
      )}

      {!isLoading && !error && !active && (
        <Typography variant="body2" color="text.secondary">
          No active vetting config found.
        </Typography>
      )}

      {!isLoading && !error && active && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {isEditing ? (
            <Box
              component="form"
              onSubmit={handleSaveConfig}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Modify Current Configuration
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    type="button"
                    variant="outlined"
                    disabled={updateConfigMutation.isPending}
                    onClick={handleCancelEdit}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={updateConfigMutation.isPending}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      bgcolor: 'primary.main',
                      '&:hover': { bgcolor: 'primary.dark' },
                    }}
                  >
                    {updateConfigMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Box>

              {formError && <Alert severity="error">{formError}</Alert>}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                  gap: 2,
                }}
              >
                <TextField
                  label="Accept Threshold"
                  type="number"
                  value={form.acceptThreshold}
                  onChange={handleFieldChange('acceptThreshold')}
                />
                <TextField
                  label="Waitlist Threshold"
                  type="number"
                  value={form.waitlistThreshold}
                  onChange={handleFieldChange('waitlistThreshold')}
                />
                <TextField
                  label="Q5 Min Dimension Score"
                  type="number"
                  value={form.q5MinDimensionScore}
                  onChange={handleFieldChange('q5MinDimensionScore')}
                />
                <TextField
                  label="Min Words / Answer"
                  type="number"
                  value={form.minWordCountPerAnswer}
                  onChange={handleFieldChange('minWordCountPerAnswer')}
                />
                <TextField
                  label="Max Short Answers Before Reject"
                  type="number"
                  value={form.maxShortAnswersBeforeReject}
                  onChange={handleFieldChange('maxShortAnswersBeforeReject')}
                />
                <TextField
                  label="Duplicate Window Days"
                  type="number"
                  value={form.duplicateWindowDays}
                  onChange={handleFieldChange('duplicateWindowDays')}
                />
                <TextField
                  label="Waitlist Reapply Days"
                  type="number"
                  value={form.waitlistReapplyDays}
                  onChange={handleFieldChange('waitlistReapplyDays')}
                />
                <TextField
                  label="Reject Reapply Days"
                  type="number"
                  value={form.rejectReapplyDays}
                  onChange={handleFieldChange('rejectReapplyDays')}
                />
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Active Config
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={`Version ${active.version}`}
                      size="small"
                      color={active.isActive ? 'success' : 'default'}
                      sx={{ fontWeight: 600, borderRadius: '6px' }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleStartEdit}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: 'primary.main',
                        '&:hover': { bgcolor: 'primary.dark' },
                      }}
                    >
                      Edit Config
                    </Button>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: 2,
                  }}
                >
                  <DetailField label="Accept Threshold" value={active.acceptThreshold} />
                  <DetailField label="Waitlist Threshold" value={active.waitlistThreshold} />
                  <DetailField label="Q5 Min Dimension Score" value={active.q5MinDimensionScore} />
                  <DetailField label="Min Words / Answer" value={active.minWordCountPerAnswer} />
                  <DetailField
                    label="Max Short Answers Before Reject"
                    value={active.maxShortAnswersBeforeReject}
                  />
                  <DetailField label="Duplicate Window Days" value={active.duplicateWindowDays} />
                  <DetailField label="Waitlist Reapply Days" value={active.waitlistReapplyDays} />
                  <DetailField label="Reject Reapply Days" value={active.rejectReapplyDays} />
                  <DetailField label="Created" value={formatVettingDateTime(active.createdAt)} />
                </Box>

                {SHOW_EVERYTHING && <DetailField label="Change Note" value={active.changeNote} />}

                {SHOW_EVERYTHING && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        System Instructions
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                      >
                        {active.systemInstructions || '-'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Dimension Weights
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {Object.entries(active.dimensionWeights ?? {}).map(([key, value]) => (
                          <Chip
                            key={key}
                            label={`${key}: ${value}`}
                            size="small"
                            sx={{ borderRadius: '6px', fontWeight: 600 }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}

          {SHOW_EVERYTHING && (
            <>
              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Question Config
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(active.questionConfig ?? []).map((question) => (
                    <Box
                      key={question.questionNumber}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Question {question.questionNumber} · Weight {question.weight}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        {question.promptText}
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Measures
                          </Typography>
                          <ListChips values={question.measures} />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Positive Signals
                          </Typography>
                          <ListChips values={question.positiveSignalKeywords} />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Red Flags
                          </Typography>
                          <ListChips values={question.redFlags} />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Auto Reject Signals
                          </Typography>
                          <ListChips values={question.autoRejectSignals} />
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  Version History
                </Typography>
                <TableContainer>
                  <Table sx={{ minWidth: 720 }}>
                    <PortalTableHead>
                      <TableRow>
                        <TableCell>Version</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Created At</TableCell>
                        <TableCell>Change Note</TableCell>
                      </TableRow>
                    </PortalTableHead>
                    <TableBody>
                      {history.length === 0 ? (
                        <PortalTableRow>
                          <PortalTableCell colSpan={5}>
                            <Typography variant="body2" color="text.secondary">
                              No version history found.
                            </Typography>
                          </PortalTableCell>
                        </PortalTableRow>
                      ) : (
                        history.map((item) => (
                          <PortalTableRow key={item.id || item.version}>
                            <PortalTableCell>{item.version ?? '-'}</PortalTableCell>
                            <PortalTableCell>
                              <Chip
                                label={item.isActive ? 'Active' : 'Inactive'}
                                size="small"
                                color={item.isActive ? 'success' : 'default'}
                                sx={{ borderRadius: '6px', fontWeight: 600 }}
                              />
                            </PortalTableCell>
                            <PortalTableCell>{item.createdBy || '-'}</PortalTableCell>
                            <PortalTableCell>
                              {formatVettingDateTime(item.createdAt)}
                            </PortalTableCell>
                            <PortalTableCell>{item.changeNote || '-'}</PortalTableCell>
                          </PortalTableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
        </Box>
      )}
    </PageContainer>
  );
};

export default VettedCommunityConfig;
